import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authAPI } from '../utils/api';
import i18n from '../i18n';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem('sophistiscan-token');
    const savedUser = localStorage.getItem('sophistiscan-user');
    if (token && savedUser) {
      try {
        const parsed = JSON.parse(savedUser);
        setUser(parsed);
        if (parsed.language) {
          i18n.changeLanguage(parsed.language);
          document.documentElement.dir = parsed.language === 'he' ? 'rtl' : 'ltr';
          document.documentElement.lang = parsed.language;
        }
      } catch {
        localStorage.removeItem('sophistiscan-token');
        localStorage.removeItem('sophistiscan-user');
      }
    }
    setLoading(false);
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await authAPI.login(email, password);
    localStorage.setItem('sophistiscan-token', data.token);
    localStorage.setItem('sophistiscan-user', JSON.stringify(data.user));
    setUser(data.user);
    if (data.user.language) {
      i18n.changeLanguage(data.user.language);
      localStorage.setItem('sophistiscan-lang', data.user.language);
      document.documentElement.dir = data.user.language === 'he' ? 'rtl' : 'ltr';
      document.documentElement.lang = data.user.language;
    }
    return data.user;
  }, []);

  const register = useCallback(async (formData) => {
    const { data } = await authAPI.register(formData);
    localStorage.setItem('sophistiscan-token', data.token);
    localStorage.setItem('sophistiscan-user', JSON.stringify(data.user));
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem('sophistiscan-token');
    localStorage.removeItem('sophistiscan-user');
    setUser(null);
  }, []);

  const switchLanguage = useCallback(async (lang) => {
    i18n.changeLanguage(lang);
    localStorage.setItem('sophistiscan-lang', lang);
    document.documentElement.dir = lang === 'he' ? 'rtl' : 'ltr';
    document.documentElement.lang = lang;
    if (user) {
      try {
        await authAPI.updateLanguage(lang);
        const updatedUser = { ...user, language: lang };
        setUser(updatedUser);
        localStorage.setItem('sophistiscan-user', JSON.stringify(updatedUser));
      } catch (e) {
        console.error('Failed to save language preference:', e);
      }
    }
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, switchLanguage }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
